import { useState, useEffect, useCallback } from 'react';
import fleetApi from '../../services/api.service';


/**
 * Hook personnalisé pour gérer la configuration générale de l'application
 * @returns {Object} - Objet contenant la configuration, les états de chargement et les fonctions
 */
const useGeneralConfig = () => {
  // État pour la configuration générale avec valeurs par défaut
  const [generalConfig, setGeneralConfig] = useState({
    average_time_per_stop: 15, // valeur par défaut en minutes
    default_start_time: '08:00', // heure par défaut
    max_km: 250,
    tour_max_duration: '08:00'
  });

  // États pour le loading et les erreurs
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Fonction pour récupérer la configuration générale
  const fetchGeneralConfig = useCallback(async (forceRefresh = false) => {
    // Éviter les appels multiples si déjà chargé récemment (cache 5 minutes)
    const now = Date.now();
    const cacheTime = 0; // 5 minutes
    if (!forceRefresh && lastFetch && (now - lastFetch) < cacheTime) {
      console.log('📋 Configuration générale utilisée depuis le cache');
      return generalConfig;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fleetApi.get('/general-configuration');
      
      if (response.status === 200 && response?.data) {
        console.log('📋 Configuration générale récupérée:', response.data);
        
        const newConfig = {
          average_time_per_stop: parseInt(response.data.average_time_per_stop, 10) || 15,
          default_start_time: response.data.default_start_time || '08:00',
          max_km: parseInt(response.data.max_km, 10) || 250,
          tour_max_duration: response.data.tour_max_duration || '08:00'
        };
        
        setGeneralConfig(newConfig);
        setLastFetch(now);
        setError(null);
        
        return newConfig;
      }
    } catch (err) {
      console.warn('⚠️ Impossible de récupérer la configuration générale, utilisation des valeurs par défaut:', err);
      setError(err);
      // Les valeurs par défaut restent dans l'état
    } finally {
      setIsLoading(false);
    }

    return generalConfig;
  }, [generalConfig, lastFetch]);

  // Fonction pour mettre à jour la configuration
  const updateGeneralConfig = useCallback(async (id, updates) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fleetApi.put(`/general-configuration/${id}`, updates);

      if (response.status === 200 && response?.data) {
        console.log('✅ Configuration générale mise à jour:', response.data);
        
        const updatedConfig = {
          average_time_per_stop: parseInt(response.data.average_time_per_stop, 10) || 15,
          default_start_time: response.data.default_start_time || '08:00',
          max_km: parseInt(response.data.max_km, 10) || 250,
          tour_max_duration: response.data.tour_max_duration || '08:00'
        };
        
        setGeneralConfig(updatedConfig);
        setLastFetch(Date.now());
        setError(null);
        
        return updatedConfig;
      }
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour de la configuration générale:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fonction pour créer une nouvelle configuration
  const createGeneralConfig = useCallback(async (configData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fleetApi.post('/general-configuration', configData);

      if (response.status === 200 && response?.data) {
        console.log('✅ Configuration générale créée:', response.data);
        
        const newConfig = {
          average_time_per_stop: parseInt(response.data.average_time_per_stop, 10) || 15,
          default_start_time: response.data.default_start_time || '08:00',
          max_km: parseInt(response.data.max_km, 10) || 250,
          tour_max_duration: response.data.tour_max_duration || '08:00'
        };
        
        setGeneralConfig(newConfig);
        setLastFetch(Date.now());
        setError(null);
        
        return newConfig;
      }
    } catch (err) {
      console.error('❌ Erreur lors de la création de la configuration générale:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Chargement automatique au montage du hook
  useEffect(() => {
    if (!lastFetch) {
      fetchGeneralConfig();
    }
  }, [fetchGeneralConfig, lastFetch]);

  // Fonction de rafraîchissement forcé
  const refreshConfig = useCallback(() => {
    return fetchGeneralConfig(true);
  }, [fetchGeneralConfig]);

  return {
    // État de la configuration
    generalConfig,
    
    // États du hook
    isLoading,
    error,
    lastFetch,
    
    // Fonctions
    fetchGeneralConfig,
    updateGeneralConfig,
    createGeneralConfig,
    refreshConfig,
    
    // Fonction utilitaire pour vérifier si la config est valide
    isConfigValid: generalConfig?.default_start_time && generalConfig?.average_time_per_stop,
  };
};

export default useGeneralConfig;

